import {
    Card,
    SectionHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ThirdPartyMessage } from '@common/interfaces/api/thirdPartyMessagesApi';
import { ThirdPartyMessageType } from '@common/enums';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import { getThirdPartyMessagesForProcess } from '@frontend/services/api/thirdPartyMessagesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';

type ThirdPartyMessageProps = Readonly<{
    processId: string;
}>;

const zorgmailThirdPartyMessageTypes = [
    ThirdPartyMessageType.medspe,
    ThirdPartyMessageType.medvri,
    ThirdPartyMessageType.medlab,
];

export function ThirdPartyMessages({ processId }: ThirdPartyMessageProps) {
    const abortSignal = useAbortSignal();
    const [thirdPartyMessages, setThirdPartyMessages] = useState<ThirdPartyMessage[]>([]);
    const translate = getSelectedLanguage();

    useEffect(() => {
        loadThirdPartyMessages();
    }, [processId]);

    if (thirdPartyMessages.length === 0) {
        return;
    }

    return (
        <>
            <SectionHeader title={translate.common.thirdPartyMessages} />
            <Card>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{translate.processThirdPartyMessages.messageType}</TableHeader>
                                <TableHeader>{translate.processThirdPartyMessages.messageSubtype}</TableHeader>
                                <TableHeader>{translate.processThirdPartyMessages.recipient}</TableHeader>
                                <TableHeader>{translate.processThirdPartyMessages.status}</TableHeader>
                                <TableHeader>{translate.processThirdPartyMessages.firstScheduledDateTime}</TableHeader>
                                <TableHeader>{translate.processThirdPartyMessages.finishedDateTime}</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {thirdPartyMessages.map((thirdPartyMessage) => {
                                let type: string = thirdPartyMessage.messageType;
                                let subtype = thirdPartyMessage.messageSubtype;
                                if (zorgmailThirdPartyMessageTypes.includes(thirdPartyMessage.messageType)) {
                                    subtype = type;
                                    type = 'Zorgmail';
                                }

                                return (
                                    <TableRow key={thirdPartyMessage.id}>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{type}</TableCell>
                                        <TableCell>{subtype}</TableCell>
                                        <TableCell>{thirdPartyMessage.recipient}</TableCell>
                                        <TableCell>
                                            {translate.processThirdPartyMessagesEnum[thirdPartyMessage.status]}
                                        </TableCell>
                                        <TableCell>
                                            <NullableFormattedDatetime
                                                value={thirdPartyMessage.firstScheduledDateTime}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <NullableFormattedDatetime value={thirdPartyMessage.finishedDateTime} />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </>
    );

    async function loadThirdPartyMessages() {
        const result = await getThirdPartyMessagesForProcess(processId, abortSignal);

        setThirdPartyMessages(result.items);
    }
}
